import { type intlFormatPropTypes } from "react-intl";
import { type Validator } from "stdnum";

import type { VatFeeProfile } from "../../app-main/selectors/fee-profile.selectors";
import type {
  FeeRegion,
  FeeType,
} from "../../app-main/selectors/fees.selectors";
import { TAX_DETAILS_TAX_ID_VALIDATORS } from "../TaxDetailsFormSection/TaxDetailsFormSection.consts";
import type { TaxIdRegion } from "../TaxDetailsFormSection/TaxDetailsFormSection.types";

import {
  TAX_DETAILS_FALLBACK_TAX_TYPE,
  TAX_DETAILS_FEE_CATEGORY,
  TAX_DETAILS_PATH,
  TAX_DETAILS_SCOPE,
  TAX_DETAILS_SCOPE_PARAM,
  TAX_DETAILS_STATUS,
  TAX_DETAILS_STATUS_BY_VALIDATION_STATUS,
  TAX_DETAILS_VAT_PROFILE_TYPE,
} from "./TaxDetails.consts";
import { TAX_DETAILS_MESSAGES } from "./TaxDetails.messages";
import type {
  TaxDetailsRouteQuery,
  TaxDetailsScope,
  TaxDetailsStatus,
  TaxIdValidationStatus,
  TaxRegistration,
} from "./TaxDetails.types";

/**
 * The bare name of a tax type — the fees API's own name, with a translated
 * generic sales tax fallback for the placeholder and a blank name.
 */
const getTaxTypeName = ({
  intl,
  taxType,
}: {
  intl: Pick<intlFormatPropTypes, "formatMessage">;
  taxType: string;
}): string =>
  taxType && taxType !== TAX_DETAILS_FALLBACK_TAX_TYPE
    ? taxType
    : intl.formatMessage(TAX_DETAILS_MESSAGES.fallbackTaxTypeName);

/**
 * Several tax types as one label — "GST/HST", "PST/RST/QST". A registration can
 * cover more than one, because the banners speak for a whole country.
 */
const formatTaxTypeNames = ({
  intl,
  taxTypes,
}: {
  intl: Pick<intlFormatPropTypes, "formatMessage">;
  taxTypes: string[];
}): string =>
  (taxTypes.length > 0 ? taxTypes : [TAX_DETAILS_FALLBACK_TAX_TYPE])
    .map((taxType) => getTaxTypeName({ intl, taxType }))
    .join("/");

/** The tax ID validator for a region. Provinces use only their own. */
const findTaxIdValidator = ({
  country,
  isSubregion,
  state = "",
}: TaxIdRegion): Validator | undefined => {
  if (!country) {
    return undefined;
  }

  const regionKey = isSubregion ? `${country}_${state}` : country;

  return TAX_DETAILS_TAX_ID_VALIDATORS[regionKey];
};

/** The tax's own short name where `stdnum` knows one. */
const getTaxIdAbbreviation = (region: TaxIdRegion): string | undefined =>
  findTaxIdValidator(region)?.abbreviation;

/** Names a tax: fee types first, then the stdnum abbreviation, then "Sales tax". */
const formatTaxTypeName = ({
  intl,
  taxIdAbbreviation,
  taxTypes,
}: {
  intl: Pick<intlFormatPropTypes, "formatMessage">;
  taxIdAbbreviation?: string;
  taxTypes: string[];
}): string => {
  const namedTaxTypes = taxTypes.filter(
    (taxType) => TAX_DETAILS_FALLBACK_TAX_TYPE !== taxType,
  );

  if (namedTaxTypes.length) {
    return formatTaxTypeNames({ intl, taxTypes: namedTaxTypes });
  }

  return taxIdAbbreviation ?? formatTaxTypeNames({ intl, taxTypes: [] });
};

/** What a registration's banner, heading and modal call its tax. */
const formatRegistrationTaxTypeName = ({
  intl,
  registration,
}: {
  intl: Pick<intlFormatPropTypes, "formatMessage">;
  registration: Pick<TaxRegistration, "taxIdAbbreviation" | "taxTypes">;
}): string =>
  formatTaxTypeName({
    intl,
    taxIdAbbreviation: registration.taxIdAbbreviation,
    taxTypes: registration.taxTypes,
  });

/** Reads a usable VAT rate off a profile. */
const getVatPercent = (vatProfile?: VatFeeProfile) =>
  Number.isFinite(vatProfile?.vat_percent)
    ? vatProfile?.vat_percent
    : undefined;

/** Guards against a validation status the mapping does not cover yet. */
const isTaxIdValidationStatus = (
  status?: string,
): status is TaxIdValidationStatus =>
  status !== undefined && status in TAX_DETAILS_STATUS_BY_VALIDATION_STATUS;

/** Maps a VAT profile to its registration status. */
const getTaxDetailsStatus = (vatProfile?: VatFeeProfile): TaxDetailsStatus => {
  if (!vatProfile?.vat_id) {
    return TAX_DETAILS_STATUS.empty;
  }

  const validationStatus = vatProfile.tax_id_validation?.status;

  return isTaxIdValidationStatus(validationStatus)
    ? TAX_DETAILS_STATUS_BY_VALIDATION_STATUS[validationStatus]
    : TAX_DETAILS_STATUS.unsupported;
};

/** Picks the registration for one slot. */
const findProfileForSlot = ({
  isSubregion,
  vatProfiles,
}: {
  isSubregion: boolean;
  vatProfiles: VatFeeProfile[];
}) =>
  vatProfiles
    .filter(
      (vatProfile) =>
        vatProfile.type === TAX_DETAILS_VAT_PROFILE_TYPE &&
        Boolean(vatProfile.is_subregion) === isSubregion,
    )
    .at(-1);

/** The region an address falls in. */
const findFeeRegion = ({
  country,
  regions,
  state,
}: {
  country?: string;
  regions: FeeRegion[];
  state?: string;
}): FeeRegion | undefined => {
  if (!country) {
    return regions.length === 1 ? regions[0] : undefined;
  }

  const regionsInCountry = regions.filter(
    (region) => region.country === country,
  );

  if (regionsInCountry.length === 1 && !regionsInCountry[0].state) {
    return regionsInCountry[0];
  }

  return regionsInCountry.find((region) => region.state === state);
};

/**
 * The taxes one region levies. Filtered by category because the same payload
 * prices processing fees, which nobody registers for — and by region because a
 * `{ country: "CA" }` response carries all thirteen provinces at once.
 */
const getRegionTaxTypes = ({
  feeTypes,
  regionId,
}: {
  feeTypes: FeeType[];
  regionId?: string;
}): FeeType[] =>
  regionId
    ? feeTypes.filter(
        (feeType) =>
          feeType.category === TAX_DETAILS_FEE_CATEGORY &&
          feeType.region_id === regionId,
      )
    : [];

/** Which of the region's taxes a registration is for, and what to call it. */
const resolveScopedTax = ({
  isSubregion,
  regionTaxTypes,
}: {
  isSubregion: boolean;
  regionTaxTypes: FeeType[];
}): { hasScopedTax: boolean; taxTypeKey?: string } => {
  const scopedTaxType = regionTaxTypes.find(
    (feeType) => Boolean(feeType.is_subregion) === isSubregion,
  );

  if (scopedTaxType) {
    return { hasScopedTax: true, taxTypeKey: scopedTaxType.name };
  }

  const federalTaxType = regionTaxTypes.find(
    (feeType) => !feeType.is_subregion,
  );

  return { hasScopedTax: false, taxTypeKey: federalTaxType?.name };
};

/** The distinct names of one level's taxes, in the order the payload lists them. */
const collectTaxTypeNames = ({
  feeTypes,
  isSubregion,
}: {
  feeTypes: FeeType[];
  isSubregion: boolean;
}): string[] => [
  ...new Set(
    feeTypes
      .filter(
        (feeType) =>
          feeType.category === TAX_DETAILS_FEE_CATEGORY &&
          Boolean(feeType.is_subregion) === isSubregion,
      )
      .map((feeType) => feeType.name)
      .filter((name): name is string => Boolean(name)),
  ),
];

/**
 * Every tax a country levies, by level — Canada's `{ federal: ["GST", "HST"],
 * subregion: ["PST", "RST", "QST"] }`.
 */
const getCountryTaxTypes = ({
  feeTypes,
}: {
  feeTypes: FeeType[];
}): { federal: string[]; subregion: string[] } => ({
  federal: collectTaxTypeNames({ feeTypes, isSubregion: false }),
  subregion: collectTaxTypeNames({ feeTypes, isSubregion: true }),
});

/** The profile's region tax, else the country's taxes if the fees are for its country. */
const resolveRegistrationTaxTypes = ({
  countryTaxTypes,
  feeTypes,
  isSubregion,
  regions,
  vatProfile,
}: {
  countryTaxTypes: string[];
  feeTypes: FeeType[];
  isSubregion: boolean;
  regions: FeeRegion[];
  vatProfile?: VatFeeProfile;
}): string[] => {
  if (vatProfile) {
    const region = findFeeRegion({
      country: vatProfile.country,
      regions,
      state: vatProfile.state,
    });

    const { taxTypeKey } = resolveScopedTax({
      isSubregion,
      regionTaxTypes: getRegionTaxTypes({ feeTypes, regionId: region?._id }),
    });

    if (taxTypeKey) {
      return [taxTypeKey];
    }

    const areFeesForProfileCountry =
      !vatProfile.country ||
      regions.some((candidate) => candidate.country === vatProfile.country);

    if (!areFeesForProfileCountry) {
      return [];
    }
  }

  return countryTaxTypes;
};

const buildTaxRegistration = ({
  country,
  isSubregion,
  taxTypes,
  vatProfile,
}: {
  country?: string;
  isSubregion: boolean;
  taxTypes: string[];
  vatProfile?: VatFeeProfile;
}): TaxRegistration => ({
  id: vatProfile?._id ?? null,
  isSubregion,
  status: getTaxDetailsStatus(vatProfile),
  // Uses the country's abbreviation, as the form does.
  taxIdAbbreviation: getTaxIdAbbreviation({ country }),
  taxTypes: taxTypes.length > 0 ? taxTypes : [TAX_DETAILS_FALLBACK_TAX_TYPE],
  vatNumber: vatProfile?.vat_id,
  vatPercent: getVatPercent(vatProfile),
});

/** Builds the guest's tax registrations: one per slot the country offers. */
const getTaxRegistrations = ({
  feeTypes,
  regions,
  vatProfiles,
}: {
  feeTypes: FeeType[];
  regions: FeeRegion[];
  vatProfiles: VatFeeProfile[];
}): TaxRegistration[] => {
  const federalProfile = findProfileForSlot({
    isSubregion: false,
    vatProfiles,
  });
  const subregionProfile = findProfileForSlot({
    isSubregion: true,
    vatProfiles,
  });

  // An empty slot is named after every province, not the other slot's: a guest
  // can hold Ontario's HST and still register for British Columbia's PST.
  const { federal, subregion } = getCountryTaxTypes({ feeTypes });

  // An empty slot sits in the other slot's country, else the one the fees were
  // fetched for.
  const feesCountry =
    federalProfile?.country || subregionProfile?.country || regions[0]?.country;

  // Whether the slot exists, asked separately from what to call it: a region that
  // levies a sub-region tax the payload left unnamed still has one to register
  // for, and `buildTaxRegistration` labels it generically rather than dropping it.
  const leviesSubregionTax = feeTypes.some(
    (feeType) =>
      feeType.category === TAX_DETAILS_FEE_CATEGORY && feeType.is_subregion,
  );

  const registrations = [
    buildTaxRegistration({
      country: federalProfile?.country || feesCountry,
      isSubregion: false,
      taxTypes: resolveRegistrationTaxTypes({
        countryTaxTypes: federal,
        feeTypes,
        isSubregion: false,
        regions,
        vatProfile: federalProfile,
      }),
      vatProfile: federalProfile,
    }),
  ];

  if (leviesSubregionTax || subregionProfile) {
    registrations.push(
      buildTaxRegistration({
        country: subregionProfile?.country || feesCountry,
        isSubregion: true,
        taxTypes: resolveRegistrationTaxTypes({
          countryTaxTypes: subregion,
          feeTypes,
          isSubregion: true,
          regions,
          vatProfile: subregionProfile,
        }),
        vatProfile: subregionProfile,
      }),
    );
  }

  return registrations;
};

const TAX_DETAILS_SCOPE_VALUES = new Set<string>(
  Object.values(TAX_DETAILS_SCOPE),
);

const isTaxDetailsScope = (scope: unknown): scope is TaxDetailsScope =>
  typeof scope === "string" && TAX_DETAILS_SCOPE_VALUES.has(scope);

/**
 * Where a vat details add / update form lives. Add vs update is inferred
 * from the store, so only the scope needs saying — and federal, being the
 * default, says nothing at all.
 */
const getTaxDetailsFormPath = ({
  isSubregion,
}: Pick<TaxRegistration, "isSubregion">): string =>
  isSubregion
    ? `${TAX_DETAILS_PATH}?${TAX_DETAILS_SCOPE_PARAM}=${TAX_DETAILS_SCOPE.subregion}`
    : TAX_DETAILS_PATH;

/**
 * Reads the scope off a route query. An absent or blank scope means federal.
 */
const parseTaxDetailsScope = (
  query?: TaxDetailsRouteQuery,
): TaxDetailsScope | null => {
  const scope = query?.[TAX_DETAILS_SCOPE_PARAM];

  if (!scope) {
    return TAX_DETAILS_SCOPE.federal;
  }

  return isTaxDetailsScope(scope) ? scope : null;
};

/**
 * Every country the guest holds a tax registration in, in the order they were
 * saved. Their fees are what price the registrations, whatever the locale — a
 * German VAT number on an en-CA account is read against German fees.
 */
const getRegistrationCountries = (vatProfiles: VatFeeProfile[]): string[] => [
  ...new Set(
    vatProfiles
      .filter((vatProfile) => vatProfile.type === TAX_DETAILS_VAT_PROFILE_TYPE)
      .map((vatProfile) => vatProfile.country)
      .filter((country): country is string => Boolean(country)),
  ),
];

/**
 * One fees payload out of several countries' responses. Regions are keyed by id,
 * so a region two responses share is listed once; fee types join onto them by
 * `region_id` and are kept as sent.
 */
const mergeRegionalFeesResponses = (
  responses: { fees?: FeeType[]; regions?: FeeRegion[] }[],
): { fees: FeeType[]; regions: FeeRegion[] } => {
  const regionsById = new Map<string, FeeRegion>();

  responses
    .flatMap((response) => response.regions ?? [])
    .forEach((region) => {
      if (!regionsById.has(region._id)) {
        regionsById.set(region._id, region);
      }
    });

  return {
    fees: responses.flatMap((response) => response.fees ?? []),
    regions: [...regionsById.values()],
  };
};

export {
  findFeeRegion,
  findProfileForSlot,
  findTaxIdValidator,
  formatRegistrationTaxTypeName,
  formatTaxTypeName,
  formatTaxTypeNames,
  getCountryTaxTypes,
  getRegionTaxTypes,
  getRegistrationCountries,
  getTaxDetailsFormPath,
  getTaxDetailsStatus,
  getTaxIdAbbreviation,
  getTaxRegistrations,
  getTaxTypeName,
  getVatPercent,
  mergeRegionalFeesResponses,
  parseTaxDetailsScope,
  resolveScopedTax,
};
